import { GameState, Player, PlayerHistoryRecord, Objective, TeamInLeague, League, GameConfig, SeasonSummary } from '../../domain/entities';
import { getPlayerWeeklyWage, calculateExpectedWage } from '../../domain/constants';
import { updateAITransferLists, performAITransfers, performAIReleases } from '../AITransferService';
import { updateAITactics } from '../TeamService';
import { CupService } from '../CupService';
import { FixturesHelper } from '../FixturesHelper';
import { addNewsItem } from '../GameService';
import { AssistantManagerBriefingService } from '../AssistantManagerBriefingService';
import { generateSeasonSchedule } from '../SeasonScheduler';
import { AIFacilityManagementService } from '../AIFacilityManagementService';
import { FreeAgentService } from '../FreeAgentService';

const sortLeague = (league: any) => {
    league.teams.sort((a: any, b: any) => {
        if (a.points !== b.points) return b.points - a.points;
        const aGD = a.goalsFor - a.goalsAgainst;
        const bGD = b.goalsFor - b.goalsAgainst;
        if (aGD !== bGD) return bGD - aGD;
        if (a.goalsFor !== b.goalsFor) return b.goalsFor - b.goalsFor;
        return a.name.localeCompare(b.name);
    });
};

const createSeasonSummary = (gameState: GameState): SeasonSummary => {
    const summary: SeasonSummary = {
        season: gameState.season,
        leagueSummaries: {},
        cupWinners: {},
    };

    // Populate league summaries
    Object.entries(gameState.leagues).forEach(([leagueName, league]) => {
        const leagueConfig = gameState.config.LEAGUE_CONFIG[leagueName];
        const promotionZone = leagueConfig.promotion || 0;
        const relegationZone = leagueConfig.relegation || 0;

        const promoted = league.teams.slice(0, promotionZone).map(t => t.name);
        const relegated = league.teams.slice(league.teams.length - relegationZone).map(t => t.name);
        const champion = league.teams[0]?.name || 'Unknown';

        // Find top scorer in league
        const allPlayers = league.teams.flatMap(t => t.squad);
        const topScorer = allPlayers.length > 0
            ? allPlayers.reduce((top, player) => {
                const playerGoals = (player.goalsScored || 0);
                const topGoals = (top.goalsScored || 0);
                return playerGoals > topGoals ? player : top;
            })
            : null;

        // Find top rated in league
        const topRated = allPlayers.length > 0
            ? allPlayers.reduce((top, player) => {
                const playerRating = (player.skill || 0);
                const topRating = (top.skill || 0);
                return playerRating > topRating ? player : top;
            })
            : null;

        summary.leagueSummaries[leagueName] = {
            promoted,
            relegated,
            champion,
            topScorer: topScorer ? { name: topScorer.name, goals: topScorer.goalsScored || 0 } : null,
            topRated: topRated ? { name: topRated.name, rating: topRated.skill || 0 } : null,
        };
    });

    // Populate cup winners
    Object.entries(gameState.cups).forEach(([cupName, cup]) => {
        summary.cupWinners[cupName] = cup.winner;
    });

    return summary;
};

export const generateTeamObjectives = (
    team: TeamInLeague,
    league: League,
    leagueNames: string[],
    config: GameConfig
): Objective[] => {
    const isTopLeague = league.name === leagueNames[leagueNames.length - 1];
    const isBottomLeague = league.name === leagueNames[0];

    const leagueAverageSkill = league.teams.reduce((sum, t) => sum + t.baseSkill, 0) / league.teams.length;
    const skillDiff = team.baseSkill - leagueAverageSkill;

    const objectives: Objective[] = [];

    // Primary League Objective
    if (isTopLeague) {
        if (skillDiff > 5) {
            objectives.push({ description: 'Win the title', isPrimary: true, type: 'league', isMet: false });
        } else if (skillDiff > -2) {
            objectives.push({ description: 'Finish in the top half', isPrimary: true, type: 'league', isMet: false });
        } else {
            objectives.push({ description: 'Avoid relegation', isPrimary: true, type: 'league', isMet: false });
        }
    } else { // Not top league
        if (skillDiff > 5) {
            objectives.push({ description: 'Gain promotion', isPrimary: true, type: 'league', isMet: false });
        } else if (skillDiff > -2) {
            objectives.push({ description: 'Challenge for a playoff position', isPrimary: true, type: 'league', isMet: false });
        } else if (!isBottomLeague) {
            objectives.push({ description: 'Avoid relegation', isPrimary: true, type: 'league', isMet: false });
        } else { // Bottom league relegation candidate
            objectives.push({ description: 'Achieve a respectable mid-table finish', isPrimary: true, type: 'league', isMet: false });
        }
    }

    // Secondary/Cup/Finance Objectives
    const secondaryObjectivesPool: Objective[] = [];

    // Cup
    if (skillDiff > 3) {
        secondaryObjectivesPool.push({ description: `Reach the latter stages of the ${config.CUP_NAMES[0]}`, isPrimary: false, type: 'cup', isMet: false });
    } else {
        secondaryObjectivesPool.push({ description: 'Have a respectable cup run', isPrimary: false, type: 'cup', isMet: false });
    }

    // Finance
    if (team.finances.balance < 0) {
        secondaryObjectivesPool.push({ description: "Improve the club's financial balance", isPrimary: false, type: 'finance', isMet: false });
    } else if (team.finances.balance > 1000000) {
        secondaryObjectivesPool.push({ description: `Increase the club's bank balance by 10%`, isPrimary: false, type: 'finance', isMet: false });
    } else {
        secondaryObjectivesPool.push({ description: 'Maintain a positive bank balance', isPrimary: false, type: 'finance', isMet: false });
    }

    // Youth development
    if (team.youthAcademy.level >= 3 && skillDiff < 5) { // Good academy, not a top team
        secondaryObjectivesPool.push({ description: 'Develop and integrate a youth player into the first team', isPrimary: false, type: 'challenge', isMet: false });
    }

    // Add up to 2 secondary objectives from the pool
    while (objectives.length < 3 && secondaryObjectivesPool.length > 0) {
        const randomIndex = Math.floor(Math.random() * secondaryObjectivesPool.length);
        objectives.push(secondaryObjectivesPool.splice(randomIndex, 1)[0]);
    }

    // Defensive check: Teams in the bottom league should never have "Avoid relegation" as their primary objective
    // (since there's no league below to be relegated to)
    if (isBottomLeague && objectives[0]?.description === 'Avoid relegation') {
        objectives[0] = { description: 'Achieve a respectable mid-table finish', isPrimary: true, type: 'league', isMet: false };
    }

    return objectives;
};

export const progressToNextSeason = (gameState: GameState): GameState => {
    let newState: GameState = JSON.parse(JSON.stringify(gameState));
    (newState as any).gameStarted = true;
    
    // 1. Player retirement
    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            const retirees: Player[] = [];
            team.squad = team.squad.filter(player => {
                const retireChance = (player.age - 32) * 0.1;
                if (player.age > 32 && Math.random() < retireChance) {
                    retirees.push(player);
                    return false;
                }
                return true;
            });
            retirees.forEach(player => {
                const playerStats = team.clubHistory.playerStats[player.id];
                if (playerStats && playerStats.appearances > 100) {
                    team.clubHistory.retiredLegends.push({
                        playerName: player.name,
                        appearances: playerStats.appearances,
                        goals: playerStats.goals,
                        retiredSeason: newState.season,
                    });
                }

                // Store full player object for viewing retired players
                if (!newState.retiredPlayers) {
                    newState.retiredPlayers = [];
                }
                newState.retiredPlayers.push(player);

                if (team.name === newState.playerTeamName) {
                    addNewsItem(newState, 'Player Retired', `${player.name} (${player.age}) has announced their retirement from professional football.`, 'retirement', {
                        player,
                        totalGames: playerStats?.appearances || 0,
                        totalGoals: playerStats?.goals || 0,
                    }, { type: 'team', title: 'My Team', activeTab: 'list' });
                }
            });
        });
    });

    // 2. Player progression/regression (BEFORE promotion/relegation to calculate wage on new league)
    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            team.squad.forEach(player => {
                player.age++; // Age players at the end of the season
                let change = 0;
                if (player.age < 23) {
                    // Young players have higher chance to improve, but penalty for being over 100 skill
                    let chanceToImprove = 0.7;
                    let maxImprovement = 1 + Math.floor(Math.random() * 4);

                    if (player.skill > 100) {
                        const skillsAbove100 = player.skill - 100;
                        const difficultyPenalty = Math.pow(0.5, skillsAbove100 / 5);
                        chanceToImprove *= difficultyPenalty;
                    }

                    change = Math.random() < chanceToImprove ? maxImprovement : 0;
                    player.skill = Math.min(120, Math.min(player.potential, player.skill + change));
                } else if (player.age < 29) {
                    // Prime years: small improvements or declines
                    let chanceToChange = 0.3;
                    let declineChance = 0.1;

                    if (player.skill > 100) {
                        const skillsAbove100 = player.skill - 100;
                        const difficultyPenalty = Math.pow(0.5, skillsAbove100 / 5);
                        chanceToChange *= difficultyPenalty;
                    }

                    change = Math.random() < chanceToChange ? (1 + Math.floor(Math.random() * 2)) : (Math.random() < declineChance ? -1 : 0);
                    player.skill = Math.min(120, player.skill + change);
                } else {
                    // Older players: more likely to decline
                    change = Math.random() < 0.8 ? -(1 + Math.floor(Math.random() * 3)) : 0;
                    player.skill = Math.min(120, player.skill + change);
                }
                player.lastSeasonChange = change;

                if (player.contract.yearsRemaining > 0) {
                    player.contract.yearsRemaining--;
                }

                // Reset fitness and morale at season start
                player.fitness = player.fitnessMax;
                player.morale = 'Happy';
                player.moraleReason = 'New season, fresh start';

                // Handle age-related decline
                if (player.age > 30) {
                    if (Math.random() < 0.3) player.fitnessMax = Math.max(70, player.fitnessMax - 1);
                    if (Math.random() < 0.2) player.fitnessRecovery = Math.max(5, player.fitnessRecovery - 1);
                }
            });

            // Handle contract expirations - convert expired players to free agents
            const expiredPlayers: { player: Player; team: TeamInLeague; league: League }[] = [];
            const league = Object.values(newState.leagues).find(l => l.teams.some(t => t.name === team.name));

            team.squad.forEach(player => {
                if (player.contract.yearsRemaining === 0) {
                    expiredPlayers.push({ player, team, league: league! });
                }
            });

            // Remove expired players from their current team
            if (expiredPlayers.length > 0) {
                team.squad = team.squad.filter(p => p.contract.yearsRemaining > 0);
            }

            // Convert expired players to free agents
            if (expiredPlayers.length > 0) {
                newState = FreeAgentService.convertToFreeAgents(newState, expiredPlayers);
            }
        });
    });

    // 3. Capture season summary BEFORE resetting player stats
    // (Player stats need to be current for accurate season statistics)
    const seasonSummary = createSeasonSummary(newState);
    if (!newState.seasonHistory) {
        newState.seasonHistory = [];
    }
    newState.seasonHistory.push(seasonSummary);

    // 3b. Update player history
    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            team.squad.forEach(player => {
                // Calculate average rating from recent ratings
                const averageRating = player.recentRatings && player.recentRatings.length > 0
                    ? player.recentRatings.reduce((sum, r) => sum + r, 0) / player.recentRatings.length
                    : 0;

                const playerHistory: PlayerHistoryRecord = {
                    season: newState.season,
                    teamName: team.name,
                    skill: player.skill,
                    gamesPlayed: player.gamesPlayed,
                    goalsScored: player.goalsScored,
                    goalsConceded: player.goalsConceded,
                    assists: player.assists,
                    yellowCards: player.yellowCards,
                    redCards: player.redCards,
                    potmAwards: player.potmAwards,
                    averageRating: parseFloat(averageRating.toFixed(1)),
                };
                if (!player.history) player.history = [];
                player.history.push(playerHistory);

                // Reset season stats
                player.gamesPlayed = 0;
                player.goalsScored = 0;
                player.goalsConceded = 0;
                player.assists = 0;
                player.yellowCards = 0;
                player.redCards = 0;
                player.yellowCardAccumulation = 0; // Reset yellow card accumulation each season
                player.potmAwards = 0;
            });
        });
    });

    // 4. Update team club history
    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            const { playerStats } = team.clubHistory;
            if (playerStats) {
                let mostApps = { name: team.clubHistory.mostAppearances.playerName, value: team.clubHistory.mostAppearances.value };
                let mostGoals = { name: team.clubHistory.mostGoals.playerName, value: team.clubHistory.mostGoals.value };

                Object.values(playerStats).forEach(stats => {
                    if (stats.appearances > mostApps.value) {
                        mostApps = { name: stats.name, value: stats.appearances };
                    }
                    if (stats.goals > mostGoals.value) {
                        mostGoals = { name: stats.name, value: stats.goals };
                    }
                });
                team.clubHistory.mostAppearances = { playerName: mostApps.name, value: mostApps.value };
                team.clubHistory.mostGoals = { playerName: mostGoals.name, value: mostGoals.value };
            }
        });
    });

    // 5. Handle promotions and relegations
    const promotions: { [league: string]: string[] } = {};
    const relegations: { [league: string]: string[] } = {};
    const leagueNames = newState.config.LEAGUE_NAMES;
    const currentLeagueConfig = newState.config.LEAGUE_CONFIG;
    
    leagueNames.forEach((leagueName) => {
        const config = currentLeagueConfig[leagueName];
        if (!config) return;
        
        const league = newState.leagues[leagueName];
        promotions[leagueName] = league.teams.slice(0, config.promotion).map(t => t.name);
        relegations[leagueName] = league.teams.slice(league.teams.length - config.relegation).map(t => t.name);
    });

    const newLeaguesState = JSON.parse(JSON.stringify(newState.leagues));

    leagueNames.forEach((leagueName) => {
        newLeaguesState[leagueName].teams = newLeaguesState[leagueName].teams.filter((t: any) => !relegations[leagueName].includes(t.name));
        newLeaguesState[leagueName].teams = newLeaguesState[leagueName].teams.filter((t: any) => !promotions[leagueName].includes(t.name));
    });

    leagueNames.forEach((leagueName, i) => {
        // Add relegated teams from the league above (if one exists)
        if (i > 0) {
            const leagueAbove = leagueNames[i - 1];
            const teamsToRelegate = (relegations[leagueAbove] || [])
                .map(name => newState.leagues[leagueAbove].teams.find(t => t.name === name))
                .filter(team => !!team);
            newLeaguesState[leagueName].teams.push(...teamsToRelegate);
        }
        // Add promoted teams from the league below (if one exists)
        if (i < leagueNames.length - 1) {
            const leagueBelow = leagueNames[i + 1];
            const teamsToPromote = (promotions[leagueBelow] || [])
                .map(name => newState.leagues[leagueBelow].teams.find(t => t.name === name))
                .filter(team => !!team);
            newLeaguesState[leagueName].teams.push(...teamsToPromote);

            // Award promotion prize money to promoted teams
            const promotionPrizeConfig = (newState.config.PRIZE_MONEY as any)[leagueBelow];
            if (promotionPrizeConfig && promotionPrizeConfig.promotion) {
                const prizeMoney = promotionPrizeConfig.promotion;
                teamsToPromote.forEach(team => {
                    if (team) {
                        team.finances.balance += prizeMoney;
                        team.finances.history.push({
                            season: newState.season,
                            week: Math.floor(newState.currentWeek),
                            event: 'Promotion Prize',
                            amount: prizeMoney
                        });
                    }
                });
            }
        }
    });

    newState.leagues = newLeaguesState;
    if (newState.leagues[newState.playerLeagueName].teams.every(t => t.name !== newState.playerTeamName)) {
        const newPlayerLeague = Object.keys(newState.leagues).find(name => newState.leagues[name].teams.some(t => t.name === newState.playerTeamName));
        if (newPlayerLeague) {
            newState.playerLeagueName = newPlayerLeague;
        }
    }

    newState.season++;
    newState.currentWeek = 1;

    // 5b. Generate season opening briefing from Assistant Manager
    const playerTeamForBriefing = newState.leagues[newState.playerLeagueName].teams.find(t => t.name === newState.playerTeamName);
    const playerLeagueForBriefing = newState.leagues[newState.playerLeagueName];
    if (playerTeamForBriefing && playerLeagueForBriefing) {
        if (!newState.assistantManagerBriefingHistory) {
            newState.assistantManagerBriefingHistory = [];
        }
        const seasonBriefing = AssistantManagerBriefingService.generateInitialBriefing(newState, playerTeamForBriefing, playerLeagueForBriefing, 'season_start');
        newState.assistantManagerBriefingHistory.push(seasonBriefing);

        addNewsItem(
            newState,
            '📧 New Season Briefing from Assistant Manager',
            'Your Assistant Manager has prepared a briefing for the new season.',
            'general',
            undefined,
            { type: 'assistant', title: 'Assistant Manager' }
        );
    }

    // 6. Reset team stats, update wages and expected wages, and set new objectives
    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            team.played = 0; team.won = 0; team.drawn = 0; team.lost = 0;
            team.goalsFor = 0; team.goalsAgainst = 0; team.points = 0;
            team.baseSkill = team.squad.length > 0 ? Math.round(team.squad.reduce((sum, p) => sum + p.skill, 0) / team.squad.length) : 0;

            // Update wages and expected wages for new season (considering new league and last season's performance)
            const starterIds = new Set(Object.values(team.tactics.lineup));
            team.squad.forEach(player => {
                const isStarter = starterIds.has(player.id);

                // For AI teams: set actual wage to expected wage for new season
                // For player team: keep existing wage but update expectations (let player negotiate)
                if (team.name !== newState.playerTeamName) {
                    player.wage = getPlayerWeeklyWage(player, league.name, newState.gameType, newState.config);
                }

                // Update expected wage for all players based on last season's performance
                player.expectedWage = calculateExpectedWage(player, isStarter, league.name, newState.gameType, newState.config);
            });

            team.seasonObjectives = generateTeamObjectives(team, league, leagueNames, newState.config);
        });
        sortLeague(league);
    });

    // 6.5. Process AI team facility upgrades and training focus assignments
    const stateAfterUpgrades = AIFacilityManagementService.processAITeamUpgrades(newState);
    Object.assign(newState.leagues, stateAfterUpgrades.leagues);

    // 7. Generate new fixtures and cups using the season scheduler
    const cupNames = newState.config.CUP_NAMES;
    newState.cups = {};
    newState.fixtures = FixturesHelper.initializeEmptyFixtures();

    // Create cup objects (scheduler will assign their weeks)
    const cupsForScheduler: { [cupName: string]: any } = {};
    cupNames.forEach(name => {
        const cup = CupService.createInitialCup(name, newState.leagues, newState.gameType, newState.config);
        newState.cups[name] = cup;
        cupsForScheduler[name] = cup;
    });

    // Use the season scheduler to generate all fixtures with proper week assignments
    const leagueTeamNames: { [leagueName: string]: string[] } = {};
    Object.values(newState.leagues).forEach((league: any) => {
        leagueTeamNames[league.name] = league.teams.map((t: any) => t.name);
    });

    const { fixtures: scheduledFixtures, cupWeeks: schedulerCupWeeks, cupRoundWeeks } = generateSeasonSchedule(cupsForScheduler, leagueTeamNames, cupNames);

    // Update cup rounds with their scheduled weeks from the scheduler BEFORE generating fixtures
    cupNames.forEach(cupName => {
        const cup = newState.cups[cupName];
        if (cup && cup.rounds) {
            cup.rounds.forEach((round: any, index: number) => {
                if (cupRoundWeeks[cupName] && cupRoundWeeks[cupName][index] !== undefined) {
                    round.week = cupRoundWeeks[cupName][index];
                }
            });
        }
    });

    // Initialize empty weeks for all cup rounds (reserves weeks even before fixtures are determined)
    newState.fixtures = FixturesHelper.initializeCupRoundWeeks(newState.fixtures, newState.cups);

    // Now generate first round fixtures with correct week assignments and store in GameState.fixtures.cups
    const cupFixturesByName: { [cupName: string]: any[] } = {};
    cupNames.forEach(name => {
        const cup = newState.cups[name];
        const firstRoundFixtures = CupService.generateRoundFixtures(cup);
        cupFixturesByName[name] = firstRoundFixtures;
        // Add cup fixtures to GameState.fixtures.cups
        newState.fixtures = FixturesHelper.addCupFixtures(newState.fixtures, name, cup.rounds[0].week, firstRoundFixtures);
    });

    // Build complete set of cup weeks from cupRoundWeeks (all rounds, not just first)
    const allCupWeeks = new Set<number>();
    cupNames.forEach(cupName => {
        if (cupRoundWeeks[cupName]) {
            Object.values(cupRoundWeeks[cupName]).forEach(week => {
                allCupWeeks.add(week);
            });
        }
    });

    // Add league fixtures (organized by week), but skip weeks reserved for any cup round
    Object.entries(scheduledFixtures).forEach(([leagueName, leagueFixtures]: [string, any]) => {
        // Group fixtures by week
        const fixturesByWeek: { [week: string]: any[] } = {};
        leagueFixtures.forEach((fixture: any) => {
            if (!allCupWeeks.has(fixture.week)) {
                if (!fixturesByWeek[fixture.week]) fixturesByWeek[fixture.week] = [];
                fixturesByWeek[fixture.week].push(fixture);
            }
        });
        // Add all week-based fixtures for this league (addLeagueFixtures handles adding to weeks automatically)
        Object.entries(fixturesByWeek).forEach(([week, weekFixtures]) => {
            newState.fixtures = FixturesHelper.addLeagueFixtures(newState.fixtures, leagueName, parseInt(week, 10), weekFixtures);
        });
    });

    // Note: Cup fixtures are already in fixtures.cups and fixtures.weeks.
    // They should NOT be added to fixtures.leagues as that creates duplicates when
    // display components combine league and cup fixtures.
    
    // 8. AI off-season transfers
    let stateAfterListsUpdate = updateAITransferLists(newState);
    const { newState: stateAfterTransfers, transferCount } = performAITransfers(stateAfterListsUpdate);

    if (transferCount > 0) {
        addNewsItem(
            stateAfterTransfers,
            'Transfer Window Summary',
            `${transferCount} transfer deals were completed across the leagues during the off-season.`,
            'transfer',
            undefined,
            { type: 'transfers', title: 'Transfers', activeTab: 'history' }
        );
    }

    // 8b. AI teams release underperforming players
    const stateAfterReleases = performAIReleases(stateAfterTransfers);

    // 9. Update AI tactics after off-season transfers and squad changes
    Object.values(stateAfterReleases.leagues).forEach(league => {
        league.teams
            .filter(team => team.name !== stateAfterReleases.playerTeamName)
            .forEach(updateAITactics);
    });

    return stateAfterReleases;
};